import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom"
import { Dropdown, Button, Skeleton } from "antd"
import { UserOutlined } from "@ant-design/icons"
import { useTranslation } from "react-i18next";
import { switchAccount } from "auth";
import { availableAccountsForUser, createAccountsForAccountSwitcher } from "api/services/authServices";
import { DEMO_MODE_ACTIVE } from "api/utils/servicesUtils";
import { APP_MODULE_KEYS } from "modules/appModuleKeys";
import { canAccessModule } from "modules/appModuleAccess";
import useAuthStore from "stores/useAuthStore";
import "./UserMenu.css";

const UserMenu = ({ onLogout, customTriggerRender }) => {
  const { t, i18n } = useTranslation("common");
  const initialAuthToken = useAuthStore((s) => s.initialAuthToken);
  const initialUserData = useAuthStore((s) => s.initialUserData);
  const userData = useAuthStore((s) => s.userData);
  const navigate = useNavigate();

  const [availableAccounts, setAvailableAccounts] = useState(null);
  const [isCreatingAccounts, setIsCreatingAccounts] = useState(false);

  const isInitialUserOwner = initialUserData?.role === "owner";
  // check if the user can access the configuration module
  const canAccessConfiguration = canAccessModule(APP_MODULE_KEYS.CONFIGURATION, userData);

  const CSS_Selected_Role = `rgba(174, 214, 241, 0.6)`;

  const roleLabels = useMemo(() => ({
    "current_user": t("roles.current_user"),
    "owner": t("roles.owner"),
    "manager": t("roles.manager"),
    "user": t("roles.user"),
    "evaluator": t("roles.evaluator"),
  }), [i18n.language]);

  const fetchUserAccounts = async () => {
    try {
      const response = await availableAccountsForUser();
      const parsedResponse = (typeof response === "object" && !Array.isArray(response) && response !== null) ? response : {};

      setAvailableAccounts((prev) => ({
        ...prev,
        ...parsedResponse
      }));
    } catch (error) {
      console.error("Couldn't fetch additional accounts: ", error);
    }
  };

  useEffect(() => {
    if (initialAuthToken && initialUserData) {
      setAvailableAccounts({
        active_user: {
          email: initialUserData.sub,
          access_token: initialAuthToken
        }
      });

      if (isInitialUserOwner && DEMO_MODE_ACTIVE) fetchUserAccounts();
    } else {
      setAvailableAccounts(null);
    }
  }, [initialAuthToken, initialUserData, isInitialUserOwner]);

  const handleSettingsClick = () => {
    navigate("/configuration")
  };

  const handleCreateAccounts = async () => {
    if (!DEMO_MODE_ACTIVE) return;
    setIsCreatingAccounts(true);
    try {
      await createAccountsForAccountSwitcher();
      await fetchUserAccounts();
    } catch (error) {
      console.error("Failed to create accounts for switcher: ", error);
    } finally {
      setIsCreatingAccounts(false);
    }
  };

  const items = useMemo(() => {
    return [
      ...(canAccessConfiguration
        ? [{
          key: "config",
          label: (
            <span style={{ display: "inline-flex", alignItems: "center" }}>
              {t("buttons.configuration")}
            </span>
          ),
          onClick: handleSettingsClick,
        },
        { type: "divider", key: "div-1" },
        ] : []
      ),

      // Impersonation options
      ...(availableAccounts
        ? [
          ...(availableAccounts?.active_user ? [{
            key: "active_user",
            label: (
              <span style={{ display: "inline-flex", alignItems: "center" }}>
                {availableAccounts.active_user?.email || roleLabels["current_user"]}
              </span>
            ),
            onClick: () => {
              switchAccount(availableAccounts.active_user?.access_token);
              window.location.reload();
            },
            style: availableAccounts.active_user?.email === userData?.email
              ? { backgroundColor: CSS_Selected_Role }
              : {},
          }] : []),
          // Role impersonation list (demo mode only)
          ...(DEMO_MODE_ACTIVE
            ? (isCreatingAccounts
              ? [{
                key: "accounts_skeleton",
                label: <Skeleton active paragraph={{ rows: 3 }} title={false} />,
                disabled: true,
              }]
              : [
                ...((availableAccounts?.owner && !availableAccounts?.owner?.message) ? [{
                  key: "owner",
                  label: (
                    <span style={{ display: "inline-flex", alignItems: "center" }}>
                      {roleLabels["owner"]}
                    </span>
                  ),
                  onClick: () => {
                    switchAccount(availableAccounts.owner?.access_token);
                    window.location.reload();
                  },
                  style: availableAccounts.owner?.email === userData?.email
                    ? { backgroundColor: CSS_Selected_Role }
                    : {},
                }] : []),
                ...((availableAccounts?.admin && !availableAccounts?.admin?.message) ? [{
                  key: "admin",
                  label: (
                    <span style={{ display: "inline-flex", alignItems: "center" }}>
                      {roleLabels["manager"]}
                    </span>
                  ),
                  onClick: () => {
                    switchAccount(availableAccounts.admin?.access_token);
                    window.location.reload();
                  },
                  style: availableAccounts.admin?.email === userData?.email
                    ? { backgroundColor: CSS_Selected_Role }
                    : {},
                }] : []),
                ...((availableAccounts?.user && !availableAccounts?.user?.message) ? [{
                  key: "user",
                  label: (
                    <span style={{ display: "inline-flex", alignItems: "center" }}>
                      {roleLabels["user"]}
                    </span>
                  ),
                  onClick: () => {
                    switchAccount(availableAccounts.user?.access_token);
                    window.location.reload();
                  },
                  style: availableAccounts.user?.email === userData?.email
                    ? { backgroundColor: CSS_Selected_Role }
                    : {},
                }] : []),
                ...((availableAccounts?.evaluator && !availableAccounts?.evaluator?.message) ? [{
                  key: "evaluator",
                  label: (
                    <span style={{ display: "inline-flex", alignItems: "center" }}>
                      {roleLabels["evaluator"]}
                    </span>
                  ),
                  onClick: () => {
                    switchAccount(availableAccounts.evaluator?.access_token);
                    window.location.reload();
                  },
                  style: availableAccounts.evaluator?.email === userData?.email
                    ? { backgroundColor: CSS_Selected_Role }
                    : {},
                }] : []),
              ]
            )
            : []),
          // Create accounts button (demo mode only)
          ...(DEMO_MODE_ACTIVE
            ? [{
              key: "create_accounts",
              className: "user-menu-create-accounts",
              label: (
                <div style={{ width: "100%", display: "flex", justifyContent: "flex-end" }}>
                  <Button
                    style={{ padding: "0px", height: "auto" }}
                    type="link"
                    size="small"
                    loading={isCreatingAccounts}
                    disabled={isCreatingAccounts}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCreateAccounts();
                    }}
                  >
                    {t("buttons.create_accounts", "Create Accounts")}
                  </Button>
                </div>
              ),
            }]
            : []),
          { type: "divider", key: "div-2" },
        ] : []
      ),

      // Always show logout button
      {
        key: "logout",
        label: (
          <span style={{ display: "inline-flex", alignItems: "center" }}>
            {t("buttons.logout")}
          </span>
        ),
        onClick: onLogout,
      },
    ]
  }, [i18n.language, canAccessConfiguration, availableAccounts, roleLabels, isCreatingAccounts]);


  return (
    <Dropdown
      menu={{ items }}
      trigger={["click"]}
      placement="bottomRight"
      overlayClassName="user-menu-dropdown"
    >
      {customTriggerRender ?? (
        <Button type="link" style={{ color: "#797979" }}>
          <UserOutlined style={{ fontSize: 20 }} />
        </Button>
      )}
    </Dropdown>
  )
};

export default UserMenu